/**
 * useFeatures — public backend feature flags (GET /api/features).
 *
 * Lets components show entry points (CTAs) only when the corresponding
 * flag-gated backend module is live. One fetch per page load, shared via a
 * module-level cache; defaults to all-off so nothing flashes in and then
 * errors if the backend module is disabled.
 */
import { useState, useEffect } from 'react';
import api from '@/utils/api';

const DEFAULT_FEATURES = { v1_singles_ri: false, v1_couple_ri: false, portrait_share: false, v2_individual: false, v2_pay_first: false, v2_email_identity: false, couple_bridge: false };

let cached = null;
let inflight = null;
let settled = false; // fetch finished (success OR failure) — defaults are final

async function fetchFeatures() {
  if (cached) return cached;
  if (!inflight) {
    inflight = api
      .get('/api/features')
      .then(({ data }) => { cached = { ...DEFAULT_FEATURES, ...data }; return cached; })
      .catch(() => DEFAULT_FEATURES)
      .finally(() => { inflight = null; settled = true; });
  }
  return inflight;
}

export default function useFeatures() {
  const [features, setFeatures] = useState(cached || DEFAULT_FEATURES);
  const [ready, setReady] = useState(!!cached || settled);

  useEffect(() => {
    if (cached || settled) return;
    let mounted = true;
    fetchFeatures().then((f) => { if (mounted) { setFeatures(f); setReady(true); } });
    return () => { mounted = false; };
  }, []);

  // `_ready` lets tri-state consumers (e.g. flag-gated route elements) hold a
  // loader instead of flashing the wrong flow while the fetch is in flight.
  return { ...features, _ready: ready };
}
